import { ReactNode, useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { generateDatadogTrackingProps } from 'toolkit/environment/environment/user-monitoring';
import { useSearchParams } from 'react-router-dom';

export interface TabDefinition {
  key: string;
  label: ReactNode;
  component: ReactNode;
  disabled?: boolean;
  ddTrackingName?: string;
  badge?: ReactNode;
}

type TabbedPanelProps = {
  tabDefinitions: TabDefinition[];
  shouldEvenflow?: boolean;
  showNoChildBorders?: boolean;
  fullWidth?: boolean;
  hideBottomBorder?: boolean;
  minimalIndicator?: boolean;
  gap?: number;
  scrollButtons?: false | 'auto';
} & (
  | { currentTabKey: string; setCurrentTabKey: (newVal: string) => void }
  | { currentTabKey?: never; setCurrentTabKey?: (newVal: string) => void }
);

const TabWrapper = ({
  children,
  value,
  tabKey,
}: {
  children: ReactNode;
  value: unknown;
  tabKey: unknown;
}) => {
  return (
    <div
      style={{ height: '100%', width: '100%', overflow: 'auto' }}
      hidden={value !== tabKey}
    >
      {children}
    </div>
  );
};

export default function TabbedPanel({
  currentTabKey,
  tabDefinitions,
  shouldEvenflow,
  showNoChildBorders,
  fullWidth,
  hideBottomBorder,
  minimalIndicator = false,
  scrollButtons = 'auto',
  setCurrentTabKey,
  gap,
}: TabbedPanelProps) {
  const [value, setValue] = useState<string>(tabDefinitions?.at(0)?.key);
  const usedValue = currentTabKey || value;
  const [searchParams, setSearchParams] = useSearchParams();
  const setAllValues = (newValue: string) => {
    setValue(newValue);
    if (setCurrentTabKey) setCurrentTabKey(newValue);
  };

  // If the tab definitions change we should make sure the current selected tab isn't disabled now.
  // If the selected tab is disabled, then switch to the first non-disabled tab.
  useEffect(() => {
    const selectedTab = tabDefinitions?.find(tb => tb.key === usedValue);
    if (!selectedTab) {
      setAllValues(tabDefinitions?.at(0)?.key);
    } else if (selectedTab.disabled) {
      const newValue = tabDefinitions?.find(tb => !tb.disabled)?.key;
      setAllValues(newValue);
    }
  }, [tabDefinitions]);

  useEffect(() => {
    if (
      searchParams.has('needId') &&
      tabDefinitions?.find(td => td.key === 'needs')
    ) {
      setAllValues('needs');
    }

    if (
      searchParams.has('feedItemExternalType') &&
      searchParams.has('feedItemExternalId') &&
      tabDefinitions?.find(td => td.key === 'activity')
    ) {
      setAllValues('activity');
    }

    if (
      searchParams.has('showGoals') &&
      tabDefinitions?.find(td => td.key === 'goals')
    ) {
      setAllValues('goals');
    }
  }, [searchParams]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        ...(gap && { gap: gap }),
        height: '100%',
        ...(fullWidth && {
          width: '100%',
        }),
      }}
    >
      <Box
        sx={{
          borderBottom: hideBottomBorder ? 'none' : '1px solid #e1e5e5',
          ...(minimalIndicator && {
            paddingX: 3,
            paddingY: 1,
          }),
          maxWidth: '100%',
        }}
      >
        <Tabs
          value={usedValue}
          variant={
            fullWidth || tabDefinitions.length === 1
              ? 'fullWidth'
              : 'scrollable'
          }
          scrollButtons={scrollButtons}
          onChange={(e, val) => {
            setAllValues(val);
            // Clear the URL parameters that cause auto-switching
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('feedItemExternalType');
            newSearchParams.delete('feedItemExternalId');
            newSearchParams.delete('needId');
            newSearchParams.delete('showGoals');
            setSearchParams(newSearchParams);
          }}
          TabIndicatorProps={{
            className: minimalIndicator ? 'minimalIndicator' : '',
          }}
        >
          {tabDefinitions.map(def => (
            <Tab
              key={def.key}
              value={def.key}
              disableRipple={true}
              disabled={def.disabled}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '18px',
                    ...(def.badge && { paddingRight: '4px' }),
                  }}
                >
                  <Typography
                    variant={minimalIndicator ? 'titleMedium' : 'labelLarge'}
                    sx={{
                      textTransform: 'none',
                      ...(minimalIndicator && {
                        color: '#0A1B2B',
                      }),
                    }}
                  >
                    {def.label}
                  </Typography>
                  {def.badge}
                </Box>
              }
              {...generateDatadogTrackingProps(def.ddTrackingName)}
              sx={{
                ...(shouldEvenflow && { width: '50%' }),
                ...(!showNoChildBorders && {
                  border: 'none',
                  borderRadius: '0px',
                }),
                ...(tabDefinitions.length === 1 && {
                  cursor: 'default',
                }),

                '&:last-child': {
                  paddingRight: '20px',
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
        {tabDefinitions.map(def => {
          return (
            <TabWrapper tabKey={def.key} key={def.key} value={usedValue}>
              {def.component}
            </TabWrapper>
          );
        })}
      </Box>
    </Box>
  );
}
